import React, { FC, ReactNode, memo, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FaInfo } from "react-icons/fa";
import { getNewDateWithOffset } from "prembid-shared-library-npm/helpers";
import { useConfigureCounter } from "prembid-shared-library-npm/hooks";
import { Countdown } from "@/components/library/Countdown";
import { AuctionTypeEnum } from "@/lib/utils/AuctionTypeEnum";
import { formatDateTime } from "@/lib/utils/General";
import { palette, radius, shadow, spacing } from "@/themes/tokens";
import Pill from "./Pill";
import CountdownTiles from "./CountdownTiles";

interface AuctionCardProps {
    imgSrc: string;
    title: string;
    description?: string;
    auctionType: string | number;
    depositAmount?: number | string;
    startDateTimeAt?: any;
    endDateTimeAt?: any;
    auctionEndDateTimeAt?: any;
    // Documents button next to the title.
    documents?: ReactNode;
    // Info button next to the chips (opens the auction info popper).
    onInfoClick?: (event: React.MouseEvent<HTMLElement>) => void;
    infoPopper?: ReactNode;
    onOpen: () => void;
    onCountdownComplete?: () => void;
    // Buttons at the bottom of the card.
    actions?: ReactNode;
}

// In the company's currency, formatted like CurrencyLabel (1,234.56).
const money = (symbol: string | undefined, value: number | string | undefined) =>
    [symbol, Number(value ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })].filter(Boolean).join(" ");

// The auction card from the mobile app: image with status, title and documents, chips, countdown tiles, dates,
// description and the actions.
const AuctionCard: FC<AuctionCardProps> = (props) => {
    const { imgSrc, title, description, auctionType, depositAmount, startDateTimeAt, endDateTimeAt, auctionEndDateTimeAt, documents, onInfoClick, infoPopper, onOpen, onCountdownComplete, actions } = props;
    const currentDateTimeOffset = useSelector((state: any) => state.settings.serverDateTimeDifference);
    const currencySymbol = useSelector((state: any) => state.settings.settings?.CurrencySymbol);
    const [aspectRatio, setAspectRatio] = useState(0);

    const start = useMemo(() => new Date(startDateTimeAt), [startDateTimeAt]);
    const end = useMemo(() => new Date(endDateTimeAt), [endDateTimeAt]);
    const now = getNewDateWithOffset(currentDateTimeOffset);

    const counter = useConfigureCounter({ start, end });
    const getCountdownNow = useCallback(() => getNewDateWithOffset(currentDateTimeOffset).getTime(), [currentDateTimeOffset]);

    const status =
        now < start ? <Pill dot variant="solid" tone="dark" label="Awaiting" />
            : now > end ? <Pill dot variant="solid" tone="danger" label="Completed" />
                : <Pill dot variant="solid" tone="success" label="Open" />;

    const dateText = (value: any) => (value ? formatDateTime(value, "dd MMMM yyyy") + " " + formatDateTime(value, "hh:mm a") : "");

    return (
        <div
            style={{
                background: palette.surface,
                borderRadius: radius.lg,
                border: `1px solid ${palette.border}`,
                boxShadow: shadow.card,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                height: "100%",
            }}
        >
            <div style={{ position: "relative" }}>
                <div
                    onClick={onOpen}
                    style={{ aspectRatio: "1.78", cursor: "pointer", backgroundColor: aspectRatio >= 1.77 ? palette.surface : palette.surfaceMuted }}
                >
                    <img
                        src={imgSrc}
                        alt=""
                        onLoad={(e) => e.currentTarget.naturalHeight && setAspectRatio(e.currentTarget.naturalWidth / e.currentTarget.naturalHeight)}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                </div>
                <div style={{ position: "absolute", top: spacing.md, left: spacing.md, pointerEvents: "none" }}>{status}</div>
            </div>

            <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.md, flex: 1 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: spacing.sm }}>
                    <div
                        title={title}
                        style={{
                            flex: 1,
                            fontSize: 17,
                            fontWeight: 700,
                            color: palette.text,
                            lineHeight: 1.3,
                            // Always two lines tall so cards in a row line up.
                            minHeight: "2.6em",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {title}
                    </div>
                    {documents}
                </div>

                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                    <Pill label={AuctionTypeEnum[auctionType?.toString()]} />
                    <Pill tone="info" label={"Deposit " + money(currencySymbol, depositAmount)} />
                    {onInfoClick && (
                        <button
                            type="button"
                            onClick={onInfoClick}
                            aria-label="Info"
                            style={{
                                width: 22,
                                height: 22,
                                borderRadius: 11,
                                padding: 0,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: palette.surfaceMuted,
                                border: `1px solid ${palette.border}`,
                                color: palette.textMuted,
                                cursor: "pointer",
                            }}
                        >
                            <FaInfo size={10} />
                        </button>
                    )}
                    {infoPopper}
                </div>

                {counter?.countdownDate ? (
                <Countdown
                    now={getCountdownNow}
                    date={counter.countdownDate}
                    onComplete={onCountdownComplete}
                    renderer={({ days, hours, minutes, seconds, completed }) => (
                        <CountdownTiles header={counter?.countdownHeader} days={days} hours={hours} minutes={minutes} seconds={seconds} completed={completed} />
                    )}
                />
                ) : (
                    <CountdownTiles header={counter?.countdownHeader} days={0} hours={0} minutes={0} seconds={0} placeholder />
                )}

                <div style={{ display: "flex", gap: spacing.md, paddingTop: spacing.sm }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: palette.textFaint, marginBottom: 2 }}>Starts</div>
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: palette.text }}>{dateText(startDateTimeAt)}</div>
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: palette.textFaint, marginBottom: 2 }}>Ends</div>
                        <div style={{ fontSize: 12.5, fontWeight: 500, color: palette.text }}>{dateText(auctionEndDateTimeAt ?? endDateTimeAt)}</div>
                    </div>
                </div>

                {/* Always three lines tall (empty when there is no description) so cards in a row line up. */}
                <div
                    style={{
                        fontSize: 13,
                        lineHeight: "18px",
                        minHeight: 54,
                        color: palette.textMuted,
                        whiteSpace: "pre-wrap",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {description}
                </div>

                {actions && <div className="pb-card-actions" style={{ marginTop: "auto" }}>{actions}</div>}
            </div>
        </div>
    );
};

export default memo(AuctionCard);
